import { useApolloClient } from '@apollo/client'
import { FavoriteBorderOutlined, FavoriteOutlined } from '@mui/icons-material'
import { styled, Tooltip, IconButton } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { BucketMedia, BucketMediaFragmentFragmentDoc } from '../../../../../graphql/generated'
import useBucketBulkMediaUpdate from '../../../hooks/useBucketBulkMediaUpdate'
import useBucketMediaFavoriteCacheUpdate from '../../../hooks/useBucketMediaFavoriteCacheUpdate'
import useSelectedMedia from '../../../hooks/useSelectedMedia'
import useBucketPlan from '../../../hooks/useBucketPlan'

const PillButton = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: '#fff',
  fontWeight: 500,
  fontSize: '0.875rem',
  cursor: 'pointer',
  gap: 8
})

export default function MediaFavoritePillButton() {
  const { restrictions, tooltipTitle } = useBucketPlan()
  const { selectedMedia } = useSelectedMedia()
  const { bucketId } = useParams<{ bucketId: string }>()
  const mediaIds: string[] = Array.from(selectedMedia.keys())
  const [isFavorite, setIsFavorite] = useState<boolean>(false)
  const { mutation: bucketBulkMediaUpdate } = useBucketBulkMediaUpdate()
  const client = useApolloClient()
  const clientRef = useRef(client)
  const { updateCache } = useBucketMediaFavoriteCacheUpdate()

  const restricted = restrictions.favorites
  const unrestrictedTitle = isFavorite ? 'Remove from Favorites' : 'Add to Favorites'
  const getTooltipTitle = () => {
    if (restricted) {
      return tooltipTitle('favorites')
    }
    return selectedMedia.size ? unrestrictedTitle : 'No Media Selected'
  }
  const title = getTooltipTitle()

  useEffect(() => {
    const stack = [...mediaIds]
    while (stack.length) {
      const mediaId = stack.pop()
      if (!mediaId) break

      const media = clientRef.current.readFragment<BucketMedia>({
        id: `BucketMedia:${mediaId}`,
        fragment: BucketMediaFragmentFragmentDoc
      })

      if (!media) break

      if (media.isFavorite) {
        setIsFavorite(media.isFavorite)
        break
      }
    }
  }, [mediaIds])

  const handleFavorite = () => {
    if (!bucketId || !mediaIds.length) return
    const variables = {
      bucketId,
      mediaIds,
      data: { isFavorite: !isFavorite }
    }
    bucketBulkMediaUpdate({
      variables,
      update: (_, results) => {
        if (!results.data?.bucketBulkMediaUpdate) return

        const updatedMediaIds = results.data.bucketBulkMediaUpdate
        updatedMediaIds.forEach((id) => {
          client.cache.modify({
            id: `BucketMedia:${id}`,
            fields: {
              isFavorite: (cachedValue) => !cachedValue
            }
          })

          const media = client.readFragment<BucketMedia>({
            id: `BucketMedia:${id}`,
            fragment: BucketMediaFragmentFragmentDoc
          })

          if (!media) return
          updateCache(media)
        })
      }
    })
    setIsFavorite(!isFavorite)
  }

  const disabled = restricted || selectedMedia.size === 0

  if (disabled) {
    return null
  }

  return (
    <Tooltip title={title} arrow>
      <PillButton as="span">
        <IconButton
          color="inherit"
          aria-label={unrestrictedTitle}
          disabled={disabled}
          data-testid="pill-favorite"
          id={isFavorite ? 'remove-favorite-pill-button' : 'add-favorite-pill-button'}
          onClick={handleFavorite}
          sx={{ color: '#fff' }}
        >
          {isFavorite ? <FavoriteOutlined /> : <FavoriteBorderOutlined />}
        </IconButton>
        <span style={{ color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#fff' }}>Favorite</span>
      </PillButton>
    </Tooltip>
  )
}
